import { ScheduleFormValues } from "./scheduleForm";
import { Frequency, Options, RRule, RRuleSet } from "rrule";
import { DailyRepeatType, getDefaultRecurrenceRule, MonthlyRepeatType, RecurrenceEndType, RecurrenceRule } from "../../types/recurrenceRule";
import { mapFromRRuleFrequency, ScheduleRecurrence } from "../../types";
import { WEEKDAY_FROM_RRULE_WEEKDAY, WEEKDAY_TO_RRULE_WEEKDAY } from "../../types/weekday";
import moment from "moment";
import { ScheduleItem } from "@lib/types";
import { convertDateToUtc } from "../../utils/convertDateToUtc";
import { getSingleItemRRule } from "../../utils/getSingleItemRRule";

/**
 * Maps form values to schedule item.
 * @param formValues Form values.
 * @param id ID for the item.
 * @param parentId Parent item ID.
 * @param isSingle Whether schedule item is single.
 */
export function mapFormValuesToScheduleItem(
    formValues: ScheduleFormValues,
    id: ScheduleItem["id"],
    parentId: ScheduleItem["id"] | null = null,
    isSingle = false,
): ScheduleItem {

    let execute: ScheduleItem["userSettings"]["execute"] = null;
    if (formValues.recurrenceRule.daily.type === DailyRepeatType.Weekends) {
        execute = "weekends";
    } else if (formValues.recurrenceRule.daily.type === DailyRepeatType.Weekdays) {
        execute = "weekdays";
    }

    return {
        id,
        color: formValues.color,
        parentId,
        name: formValues.name,
        priority: formValues.priority,
        action: {
            type: formValues.action,
            parameter: formValues.parameter,
            parameterId: formValues.parameterId,
            pulseDuration: formValues.impulseDuration,
            value: formValues.value,
        },
        rrule: isSingle ?
            getSingleItemRRule(formValues.dateTime) :
            generateRRule(formValues),
        startDate: formValues.dateTime,
        endDate: formValues.recurrenceRule.end.type === RecurrenceEndType.Date ?
            new Date(formValues.recurrenceRule.end.date.getTime() + formValues.recurrenceRule.end.date.getTimezoneOffset() * 60 * 1000) :
            null,
        userSettings: {
            execute,
        },
        enabled: formValues.isEnabled,
        comment: formValues.comment,
        writeToArchive: formValues.writeToArchive,
        archiveMessage: formValues.writeToArchive ?
            formValues.archiveMessage :
            "",
    };
}

/**
 * Generates RRule from form values.
 * @param formValues Form values.
 */
function generateRRule(formValues: ScheduleFormValues): RRuleSet {
    const options: Partial<Options> = {
        dtstart: convertDateToUtc(formValues.dateTime),
        ...getRecurrenceEndOptions(formValues.recurrenceRule),
        ...getRecurrenceOptionsByType(formValues.recurrenceRule),
    };
    const rrule = new RRule(options);
    const set = new RRuleSet();
    set.rrule(rrule);
    return set;
}

/**
 * Get RRule end options from recurrence rule.
 * @param rule Recurrence rule.
 */
function getRecurrenceEndOptions(rule: RecurrenceRule): Partial<Options> {
    const { type, date } = rule.end;
    if (rule.recurrence === ScheduleRecurrence.NoRepeat) {
        return {
            count: 1,
        };
    }
    if (type === RecurrenceEndType.Never) {
        return {};
    }
    return {
        until: date,
    };
}

/**
 * Gets RRule options from recurrence rule.
 * @param rule Recurrence rule.
 */
function getRecurrenceOptionsByType(rule: RecurrenceRule): Partial<Options> {
    switch (rule.recurrence) {
        case ScheduleRecurrence.Hourly:
            return getHourlyRecurrenceOptions(rule);
        case ScheduleRecurrence.Daily:
            return getDailyRecurrenceOptions(rule);
        case ScheduleRecurrence.Weekly:
            return getWeeklyRecurrenceOptions(rule);
        case ScheduleRecurrence.Monthly:
            return getMonthlyRecurrenceOptions(rule);
        case ScheduleRecurrence.NoRepeat:
        default:
            return {
                freq: Frequency.YEARLY,
            };
    }
}

/**
 * Gets hourly RRule options from recurrence rule.
 * @param rule Recurrence rule.
 */
function getHourlyRecurrenceOptions(rule: RecurrenceRule): Partial<Options> {
    return {
        freq: Frequency.HOURLY,
        interval: rule.hourly.hourNumber,
    };
}

/**
 * Gets daily RRule options from recurrence rule.
 * @param rule Recurrence rule.
 */
function getDailyRecurrenceOptions(rule: RecurrenceRule): Partial<Options> {
    const baseOptions: Partial<Options> = {
        freq: Frequency.DAILY,
    };
    const { daily } = rule;
    if (daily.type === DailyRepeatType.DayNumber) {
        return {
            ...baseOptions,
            interval: daily.dayNumber,
        };
    } else if (daily.type === DailyRepeatType.Weekdays) {
        return {
            ...baseOptions,
            byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
        };
    }
    return {
        ...baseOptions,
        byweekday: [RRule.SA, RRule.SU],
    };
}

/**
 * Gets weekly RRule options from recurrence rule.
 * @param rule Recurrence rule.
 */
function getWeeklyRecurrenceOptions(rule: RecurrenceRule): Partial<Options> {
    const { weekly } = rule;
    return {
        freq: Frequency.WEEKLY,
        interval: weekly.weekNumber,
        byweekday: weekly.days.map(weekday => WEEKDAY_TO_RRULE_WEEKDAY[weekday]),
    };
}

/**
 * Gets monthly RRule options from recurrence rule.
 * @param rule Recurrence rule.
 */
function getMonthlyRecurrenceOptions(rule: RecurrenceRule): Partial<Options> {
    const baseOptions: Partial<Options> = {
        freq: Frequency.MONTHLY,
    };
    const { monthly } = rule;
    if (monthly.type === MonthlyRepeatType.Day) {
        return {
            ...baseOptions,
            bymonthday: monthly.day.dayNumber,
            interval: monthly.day.monthNumber,
        };
    }
    return {
        ...baseOptions,
        interval: monthly.weekday.monthNumber,
        byweekday: WEEKDAY_TO_RRULE_WEEKDAY[monthly.weekday.weekday].nth(monthly.weekday.weekdayNumber),
    };
}

/**
 * Maps schedule item to form values.
 * @param item Schedule item.
 * @param parentItemRRule Parent schedule item RRule.
 */
export function mapScheduleItemToFormValues(item: ScheduleItem, parentItemRRule?: ScheduleItem["rrule"]): ScheduleFormValues {

    const rrule = item.rrule.rrules()[0];

    const dateTime = rrule.options.dtstart;

    return {
        name: item.name,
        color: item.color,
        priority: item.priority,
        action: item.action.type,
        parameter: item.action.parameter,
        parameterId: item.action.parameterId,
        dateTime: new Date(dateTime.getTime() + dateTime.getTimezoneOffset() * 60 * 1000),
        date: moment(dateTime).utc()
            .format("DD.MM.YYYY"),
        time: moment(dateTime).utc()
            .format("HH:mm:ss"),
        impulseDuration: item.action.pulseDuration,
        value: item.action.value,
        recurrenceRule: getRecurrenceRuleFromRRule(parentItemRRule?.rrules()[0] ?? rrule),
        isEnabled: item.enabled,
        comment: item.comment,
        writeToArchive: item.writeToArchive,
        archiveMessage: item.archiveMessage,
        isSingle: parentItemRRule != null,
    };
}

/**
 * Gets recurrence rule from RRule instance.
 * @param rrule RRule.
 */
function getRecurrenceRuleFromRRule(rrule: RRule): RecurrenceRule {
    return {
        ...getDefaultRecurrenceRule(),
        recurrence: mapFromRRuleFrequency(rrule.options.freq),
        ...getRuleByType(rrule),
        end: getEndRule(rrule),
    };
}

/**
 * Gets recurrence rule from RRUle instance by type.
 * @param rrule RRule instance.
 */
function getRuleByType(rrule: RRule): Partial<Pick<RecurrenceRule, "hourly" | "daily" | "weekly" | "monthly">> {
    switch (rrule.options.freq) {
        case Frequency.HOURLY:
            return {
                hourly: getHourlyRecurrenceRule(rrule),
            };
        case Frequency.DAILY:
            return {
                daily: getDailyRecurrenceRule(rrule),
            };
        case Frequency.WEEKLY:
            return {
                weekly: getWeeklyRecurrenceRule(rrule),
            };
        case Frequency.MONTHLY:
            return {
                monthly: getMonthlyRecurrenceRule(rrule),
            };
        case Frequency.MINUTELY:
        case Frequency.SECONDLY:
        case Frequency.YEARLY:
        default:
            return {};
    }
}

/**
 * Gets hourly recurrence rule from RRule instance.
 * @param rrule RRule instance.
 */
function getHourlyRecurrenceRule(rrule: RRule): RecurrenceRule["hourly"] {
    return {
        hourNumber: rrule.options.interval,
    };
}

/**
 * Gets daily recurrence rule from RRule instance.
 * @param rrule RRule instance.
 */
function getDailyRecurrenceRule(rrule: RRule): RecurrenceRule["daily"] {
    const weekdays = rrule.options.byweekday;
    if (weekdays === null) {
        return {
            type: DailyRepeatType.DayNumber,
            dayNumber: rrule.options.interval,
        };
    }
    if (weekdays.length === 5) {
        return {
            type: DailyRepeatType.Weekdays,
            dayNumber: 1,
        };
    }
    return {
        type: DailyRepeatType.Weekends,
        dayNumber: 1,
    };
}

/**
 * Gets weekly recurrence rule from RRule instance.
 * @param rrule RRule instance.
 */
function getWeeklyRecurrenceRule(rrule: RRule): RecurrenceRule["weekly"] {
    return {
        weekNumber: rrule.options.interval,
        days: rrule.options.byweekday.map(day => WEEKDAY_FROM_RRULE_WEEKDAY[day]),
    };
}

/**
 * Gets monthly recurrence rule from RRule instance.
 * @param rrule RRule instance.
 */
function getMonthlyRecurrenceRule(rrule: RRule): RecurrenceRule["monthly"] {
    const repeatType = rrule.options.bymonthday.length > 0 ?
        MonthlyRepeatType.Day :
        MonthlyRepeatType.Weekday;
    const [weekday, weekdayNumber] = rrule.options.bynweekday?.[0] ?? [0, 1];
    return {
        type: repeatType,
        day: {
            dayNumber: rrule.options.bymonthday[0] ?? 1,
            monthNumber: rrule.options.interval ?? 1,
        },
        weekday: {
            monthNumber: rrule.options.interval ?? 1,
            weekday: WEEKDAY_FROM_RRULE_WEEKDAY[weekday],
            weekdayNumber,
        },
    };
}

/**
 * Gets end rule from RRule instance.
 * @param rrule RRule instance.
 */
function getEndRule(rrule: RRule): RecurrenceRule["end"] {
    if (rrule.options.until !== null) {
        return {
            type: RecurrenceEndType.Date,
            date: rrule.options.until,
        };
    }
    const startDate = rrule.options.dtstart;
    const initialEndDate = moment(startDate).add(1, "d")
        .toDate();
    return {
        type: RecurrenceEndType.Never,
        date: initialEndDate,
    };
}
